import { ProfileType } from '../../enums/ProfileType';
import { StateOrderIdentification } from '../../enums/StateOrderIdentification';
import { ProfileHelper } from '../../helpers/ProfileHelper';
import {
  OrderCorrectionAddressPatch,
  OrderCorrectionGatewayResponse,
  OrderCorrectionOperation,
  OrderCorrectionTarget
} from '../../services/OrderCorrections/OrderCorrectionModels';

const correctionWasDefinitelyNotSent = new Set([
  'ORDER_CORRECTIONS_NOT_CONFIGURED',
  'ORDER_CORRECTIONS_INVALID_CONFIGURATION',
  'QUATRO_OLHOS_AUTHENTICATION_FAILED'
]);

export const userCanManageOrderCorrections = (
  enabled?: boolean,
  profile?: ProfileType
) => enabled === true && ProfileHelper.profilesThatCanManageTransport.some(allowedProfile => allowedProfile === profile);

export const orderIsPendingInvoiceRegistration = (stateId?: number) =>
  stateId === StateOrderIdentification.PendenteRegistroOmie;

export const orderIsPendingInvoiceTransmission = (stateId?: number) =>
  stateId === StateOrderIdentification.PedidoFaturando;

export const orderIsCorrectionCandidate = (stateId?: number) =>
  orderIsPendingInvoiceRegistration(stateId) || orderIsPendingInvoiceTransmission(stateId);

export const canOfferOrderCorrection = (
  enabled: boolean | undefined,
  profile: ProfileType | undefined,
  stateId: number | undefined
) => userCanManageOrderCorrections(enabled, profile) && orderIsCorrectionCandidate(stateId);

export const targetIsInvoiceRecovery = (target?: string) =>
  target === OrderCorrectionTarget.InvoiceDetailsRefresh ||
  target === OrderCorrectionTarget.InvoiceDetailsRefreshAndTransmit;

export const actionIsAllowedForOrderLifecycle = (
  target: string,
  createdOnProtheus?: boolean,
  invoiced?: boolean
) => targetIsInvoiceRecovery(target) || (!createdOnProtheus && !invoiced);

export const invoiceRecoveryOperationIsPartial = (operation?: OrderCorrectionOperation) =>
  operation?.detailsUpdated === true &&
  operation.transmissionRequested === true &&
  operation.transmitted === false;

export const orderCorrectionOperationNeedsAttention = (operation: OrderCorrectionOperation) => {
  if (invoiceRecoveryOperationIsPartial(operation)) return false;

  const status = operation.status.toLowerCase();
  return status !== 'succeeded' && status !== 'failed';
};

/**
 * Depois que um comando mutável saiu da tela, uma falha 5xx sem operação pode
 * significar que apenas a resposta se perdeu. Mantemos o comando original em
 * memória e retomamos pela mesma chave idempotente.
 */
export const mutationOutcomeIsUnconfirmed = <TData>(
  gateway?: OrderCorrectionGatewayResponse<TData>
) => {
  if (gateway?.data) return false;
  if (!gateway) return true;
  if (gateway.statusCode >= 200 && gateway.statusCode < 300) return true;
  if (correctionWasDefinitelyNotSent.has(gateway.error?.code || '')) return false;
  return gateway.statusCode >= 500;
};

export const recoveryConfirmedNotStarted = <TData>(
  gateway?: OrderCorrectionGatewayResponse<TData>
) => gateway?.success === false &&
  !gateway.data &&
  gateway.error?.code === 'RECOVERY_NOT_STARTED';

export const completeProtheusStreetNumberPair = (
  target: string,
  patch: OrderCorrectionAddressPatch,
  finalAddress: OrderCorrectionAddressPatch
): { patch: OrderCorrectionAddressPatch; error?: string } => {
  if (target !== OrderCorrectionTarget.ProtheusCustomer) return { patch };

  const changesStreet = Object.prototype.hasOwnProperty.call(patch, 'street');
  const changesNumber = Object.prototype.hasOwnProperty.call(patch, 'number');
  if (!changesStreet && !changesNumber) return { patch };

  const street = finalAddress.street;
  const number = finalAddress.number;
  if (!String(street ?? '').trim() || !String(number ?? '').trim()) {
    return {
      patch,
      error: 'Logradouro e número devem ser informados juntos para atualizar o cliente no Protheus.'
    };
  }

  if (`${String(street).trim()} ${String(number).trim()}`.length > 60) {
    return {
      patch,
      error: 'Logradouro e número juntos devem ter no máximo 60 caracteres no Protheus.'
    };
  }

  return {
    patch: {
      ...patch,
      street,
      number
    }
  };
};

export const validateOrderCorrectionAddressPatch = (
  target: string,
  patch: OrderCorrectionAddressPatch
): string | undefined => {
  if (patch.state !== undefined && patch.state !== null && !/^[A-Za-z]{2}$/.test(patch.state.trim())) {
    return 'UF deve conter exatamente duas letras.';
  }

  if (patch.zipCode !== undefined && patch.zipCode !== null) {
    const zipDigits = patch.zipCode.replace(/\D/g, '');
    if (zipDigits.length !== 8) return 'CEP deve conter oito dígitos.';
  }

  if (patch.countryCode !== undefined && patch.countryCode !== null) {
    const countryCode = patch.countryCode.trim();
    if (target === OrderCorrectionTarget.ProtheusCustomer && !/^\d{1,3}$/.test(countryCode)) {
      return 'País no Protheus deve ser um código numérico de um a três dígitos.';
    }
    if (target === OrderCorrectionTarget.Shopify && !/^[A-Za-z]{2}$/.test(countryCode)) {
      return 'País na Shopify deve conter exatamente duas letras ISO, como BR.';
    }
  }

  return undefined;
};
